import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import {
  VuiFormGroup,
  VuiButton,
  VuiIconInput,
} from '@topkamera/viso-ui';
import {
  createSideBarCustomShortcut,
  updateSideBarCustomShortcut,
} from 'api/workspace';
import { EnumType } from 'json-to-graphql-query';
import SidebarSelectIcon from './SidebarSelectIcon';
import { SidebarPopUp } from './SidebarPopUp';
import { getPermission } from 'utils/permissions';
import { isWorkspaceFeatureEnabled } from 'utils/workspaceFeatures';

const SidebarShortcutPopUp = ({
  isVisible = false,
  CreateShortcutPopUpProps,
  UpdateShortcutPopUpProps,
  linkOptions,
  mutationArgs,
  onClose,
  onSuccess,
  isUpdate,
  workspace,
  detailRole,
}) => {
  const [linkedPage, setLinkedPage] = useState([])
  const [shortcutName, setShortcutName] = useState('')
  const [shortcutIcon, setShortcutIcon] = useState('profile')
  const [shortcutType, setShortcutType] = useState(null)
  const [isSelectIconVisible, setIsSelectIconVisible] = useState(false)
  const linkedPageRef = useRef([])
  const shortcutNameRef = useRef('')
  const shortcutIconRef = useRef('profile')
  const shortcutTypeRef = useRef(null)
  const lastInitKeyRef = useRef(null)


  const dashboardPermission = getPermission(
    'permissionDashboard',
    detailRole.role,
  );

    const incidentPermission = getPermission(
    'permissionIncidents',
    detailRole.role,
  );

  const reportPermission = getPermission(
    'permissionReports',
    detailRole.role,
  );

  const hasDashboardPermission = dashboardPermission.includes('read') && isWorkspaceFeatureEnabled(workspace, 'dashboards');
  const hasIncidentPermission = incidentPermission.includes('read') && isWorkspaceFeatureEnabled(workspace, 'incidents');
  const hasReportPermission = reportPermission.includes('read') && isWorkspaceFeatureEnabled(workspace, 'reports');
  
  const resolveTypeFromUrl = useCallback((url) => {
    if (!url || typeof url !== 'string') {
      return null
    }

    if (url.includes('/dashboards')) {
      return 'dashboards'
    }

    if (url.includes('/incident')) {
      return 'incidents'
    }

    if (url.includes('/reports')) {
      return 'reports'
    }

    return null
  }, [])

  const getOptionLabel = useCallback((option) => {
    if (!option || typeof option !== 'object') {
      return ''
    }

    const label = option.label ?? option.name
    return typeof label === 'string' ? label : ''
  }, [])

  const resolveSelectValue = useCallback(
    (rawValue) => {
      const options = (linkOptions && linkOptions.length ? linkOptions : [])

      const rawAsArray = Array.isArray(rawValue) ? rawValue : (rawValue ? [rawValue] : [])
      const first = rawAsArray.length ? rawAsArray[0] : null

      if (!first) {
        return []
      }

      if (typeof first === 'object' && first.value) {
        return [first]
      }

      if (typeof first === 'string') {
        const match = options.find((o) => o && o.value === first)
        if (match) {
          return [match]
        }

        return first ? [{ label: first, value: first }] : []
      }

      return []
    },
    [linkOptions],
  )

  useEffect(() => {
    if (!isVisible) {
      lastInitKeyRef.current = null
      return
    }

    const initKey = `${isUpdate ? 'update' : 'create'}:${mutationArgs && mutationArgs.id ? mutationArgs.id : ''}`
    if (lastInitKeyRef.current === initKey) {
      return
    }

    lastInitKeyRef.current = initKey

    const nextLinkedPage = resolveSelectValue(mutationArgs && mutationArgs.url)
    setLinkedPage(nextLinkedPage)
    linkedPageRef.current = nextLinkedPage

    const resolvedLinked = resolveSelectValue(nextLinkedPage)
    const selectedOption = resolvedLinked.length ? resolvedLinked[0] : null
    const selectedLabel = getOptionLabel(selectedOption)
    const existingName = (mutationArgs && typeof mutationArgs.name === 'string' ? mutationArgs.name : '')
    const nextName = isUpdate && existingName && selectedLabel && existingName === selectedLabel ? '' : existingName

    setShortcutName(nextName)
    shortcutNameRef.current = nextName

    const nextIcon = (mutationArgs && typeof mutationArgs.icon === 'string' && mutationArgs.icon) ? mutationArgs.icon : 'profile'
    setShortcutIcon(nextIcon)
    shortcutIconRef.current = nextIcon

    const selectedOptionType = nextLinkedPage && nextLinkedPage.length ? nextLinkedPage[0]?.category : null
    const nextType = (mutationArgs && typeof mutationArgs.type === 'string' && mutationArgs.type)
      ? mutationArgs.type
      : (selectedOptionType || resolveTypeFromUrl(mutationArgs && mutationArgs.url))
    setShortcutType(nextType)
    shortcutTypeRef.current = nextType
  }, [getOptionLabel, isUpdate, isVisible, mutationArgs, resolveSelectValue, resolveTypeFromUrl])

  const getResolvedArgs = () => {
    const resolvedSelect = resolveSelectValue(linkedPageRef.current)
    const selectedOption = resolvedSelect.length ? resolvedSelect[0] : null

    return {
      ...(mutationArgs || {}),
      name: shortcutNameRef.current ? shortcutNameRef.current : (selectedOption?.label || ''),
      ...(selectedOption && selectedOption.value ? { url: selectedOption.value } : {}),
      ...(shortcutIconRef.current ? { icon: shortcutIconRef.current } : {}),
      ...(shortcutTypeRef.current ? { type: new EnumType(shortcutTypeRef.current) } : {}),
    }
  }

  const canUseIconInput = typeof VuiIconInput === 'function'

  return (
    <SidebarPopUp
      title={isUpdate ? "Update shortcut" : "Add shortcut to section"}
      description={!isUpdate && "Select an Item to add a shortcut, and set a custom title."}
      mutationParams={{
        failure: isUpdate ? "Couldn't update the shortcut." : "Couldn't create the shortcut.",
        fn: isUpdate ? updateSideBarCustomShortcut : createSideBarCustomShortcut,
        args: getResolvedArgs,
      }}
      isVisible={isVisible}
      onClose={onClose}
      popUpProps={isUpdate ? UpdateShortcutPopUpProps : CreateShortcutPopUpProps}
      onSuccess={onSuccess}
      confirmDisabled={linkedPage.length === 0 || !shortcutType}
    >
      <div className={classNames('vui-flex vui-flex-grow', !isUpdate && 'vui-pt-5')}>
        <VuiFormGroup
          fieldProps={{
            elementId: 'popoverRoot',
            scrollHeight: 6,
            isBadge: true,
            hasSearch: true,
            onChange: (value) => {
              const resolved = resolveSelectValue(value)
              linkedPageRef.current = resolved
              setLinkedPage(resolved)

              const selectedOption = resolved && resolved.length ? resolved[0] : null
              const derivedType = (selectedOption && selectedOption.category)
                ? selectedOption.category
                : resolveTypeFromUrl(selectedOption && selectedOption.value)
              shortcutTypeRef.current = derivedType
              setShortcutType(derivedType)
            },
            placeholder: 'Linked page',
            value: linkedPage || [],
            options: (linkOptions && linkOptions.length ? linkOptions : []),
            type: 'group',
            typeProps: {
              categories: [
                ... (hasDashboardPermission ? [{
                  label: 'Dashboards',
                  value: 'dashboards',
                }] : []),
                ...(hasIncidentPermission ? [{
                  label: 'Incident Boards',
                  value: 'incidents',
                }] : []),
                ...(hasReportPermission ? [{
                  label: 'Reports',
                  value: 'reports',
                }] : []),
              ],
              fixedHeight: 200,
            },
          }}
          fieldType="selectinput"
          id="linkedPage"
        />
      </div>
      <div className='vui-flex vui-gap-8'>
        {canUseIconInput ? (
          <VuiIconInput
            icon={shortcutIcon}
            size="xs"
            onClick={() => setIsSelectIconVisible(true)}
          />
        ) : (
          <VuiButton
            color="accent0"
            iconLeft={shortcutIcon}
            onClick={() => setIsSelectIconVisible(true)}
            iconSize='xs'
            radius="rounded"
            style="outline"
          />
        )}
        <VuiFormGroup
          fieldProps={{
            onChange: (e) => {
              const nextValue = e?.target?.value ?? e ?? ''
              shortcutNameRef.current = nextValue
              setShortcutName(nextValue)
            },
            placeholder: linkedPage?.[0]?.label || 'Display text',
            value: shortcutName
          }}
          groupLayout="block"
          fieldType="textinput"
          id="shortcutLabel"
        />
      </div>
      <SidebarSelectIcon
        isVisible={isSelectIconVisible}
        initialValue={shortcutIcon}
        onClose={() => setIsSelectIconVisible(false)}
        onApply={(icon) => {
          const resolvedIcon = typeof icon === 'string' && icon ? icon : 'profile'
          shortcutIconRef.current = resolvedIcon
          setShortcutIcon(resolvedIcon)
        }}
      />
    </SidebarPopUp>
  )
}

export default SidebarShortcutPopUp